'use client';

import { Box, Typography, Button, Stack, Paper, alpha, CircularProgress } from '@mui/material';
import {
  WifiOff,
  CloudOff,
  ErrorOutline,
  LockOutlined,
  PersonOff,
  Block,
  HourglassEmpty,
  VerifiedUser,
  Refresh,
  ArrowBack,
  SupportAgent,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import { ERROR_TYPES, ERROR_MESSAGES } from '@/constants/errorTypes';

// Icon mapping for error types
const ERROR_ICONS = {
  [ERROR_TYPES.NETWORK_ERROR]: WifiOff,
  [ERROR_TYPES.NETWORK_TIMEOUT]: CloudOff,
  [ERROR_TYPES.NETWORK_OFFLINE]: WifiOff,
  [ERROR_TYPES.SERVER_ERROR]: ErrorOutline,
  [ERROR_TYPES.DATABASE_ERROR]: ErrorOutline,
  [ERROR_TYPES.SERVICE_UNAVAILABLE]: CloudOff,
  [ERROR_TYPES.SESSION_EXPIRED]: LockOutlined,
  [ERROR_TYPES.UNAUTHORIZED]: LockOutlined,
  [ERROR_TYPES.ACCESS_REVOKED]: Block,
  [ERROR_TYPES.INVALID_TOKEN]: LockOutlined,
  [ERROR_TYPES.ACCOUNT_SUSPENDED]: PersonOff,
  [ERROR_TYPES.ACCOUNT_LOCKED]: Block,
  [ERROR_TYPES.ACCOUNT_DELETED]: PersonOff,
  [ERROR_TYPES.AWAITING_VERIFICATION]: HourglassEmpty,
  [ERROR_TYPES.KYC_PENDING]: VerifiedUser,
  [ERROR_TYPES.KYC_REJECTED]: VerifiedUser,
  [ERROR_TYPES.RATE_LIMITED]: HourglassEmpty,
  [ERROR_TYPES.TOO_MANY_REQUESTS]: HourglassEmpty,
  [ERROR_TYPES.FORBIDDEN]: Block,
  [ERROR_TYPES.INSUFFICIENT_PERMISSIONS]: Block,
  [ERROR_TYPES.NOT_FOUND]: ErrorOutline,
  [ERROR_TYPES.UNKNOWN_ERROR]: ErrorOutline,
};

// Color mapping for error types
const ERROR_COLORS = {
  [ERROR_TYPES.NETWORK_ERROR]: '#ef4444',
  [ERROR_TYPES.NETWORK_OFFLINE]: '#f97316',
  [ERROR_TYPES.SERVER_ERROR]: '#ef4444',
  [ERROR_TYPES.SESSION_EXPIRED]: '#f59e0b',
  [ERROR_TYPES.UNAUTHORIZED]: '#f59e0b',
  [ERROR_TYPES.ACCOUNT_SUSPENDED]: '#dc2626',
  [ERROR_TYPES.AWAITING_VERIFICATION]: '#3b82f6',
  [ERROR_TYPES.KYC_PENDING]: '#3b82f6',
  [ERROR_TYPES.FORBIDDEN]: '#dc2626',
  default: '#6b7280',
};

/**
 * Compact inline error display for cards/sections
 */
export function ErrorInline({
  errorType = ERROR_TYPES.UNKNOWN_ERROR,
  message,
  onRetry,
  retrying = false,
}) {
  const config = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR];
  const IconComponent = ERROR_ICONS[errorType] || ErrorOutline;
  const color = ERROR_COLORS[errorType] || ERROR_COLORS.default;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        bgcolor: alpha(color, 0.08),
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.2)}`,
      }}
    >
      <IconComponent sx={{ color, fontSize: 24 }} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 600, color, fontSize: '0.9rem' }}>{config.title}</Typography>
        <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
          {message || config.message}
        </Typography>
      </Box>
      {config.recoverable && onRetry && (
        <Button
          size="small"
          onClick={onRetry}
          disabled={retrying}
          startIcon={retrying ? <CircularProgress size={16} /> : <Refresh sx={{ fontSize: 16 }} />}
          sx={{
            color,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: alpha(color, 0.1) },
          }}
        >
          {retrying ? 'Retrying...' : config.action}
        </Button>
      )}
    </Box>
  );
}

/**
 * Full page error display for critical errors
 */
export function ErrorPage({
  errorType = ERROR_TYPES.UNKNOWN_ERROR,
  message,
  onRetry,
  retrying = false,
  showBackButton = true,
}) {
  const router = useRouter();
  const config = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR];
  const IconComponent = ERROR_ICONS[errorType] || ErrorOutline;
  const color = ERROR_COLORS[errorType] || ERROR_COLORS.default;

  const handleAction = () => {
    if (config.redirectTo) {
      router.push(config.redirectTo);
    } else if (onRetry) {
      onRetry();
    } else {
      router.back();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 400,
          width: '100%',
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: alpha(color, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <IconComponent sx={{ fontSize: 40, color }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
          {config.title}
        </Typography>
        <Typography sx={{ color: '#64748b', mb: 4, lineHeight: 1.6 }}>
          {message || config.message}
        </Typography>

        <Stack spacing={2}>
          <Button
            variant="contained"
            onClick={handleAction}
            disabled={retrying}
            startIcon={
              retrying ? (
                <CircularProgress size={18} color="inherit" />
              ) : config.redirectTo?.includes('login') ? (
                <LoginIcon />
              ) : config.redirectTo?.includes('support') ? (
                <SupportAgent />
              ) : config.recoverable ? (
                <Refresh />
              ) : (
                <ArrowBack />
              )
            }
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: color,
              '&:hover': { bgcolor: alpha(color, 0.8) },
            }}
          >
            {retrying ? 'Please wait...' : config.action}
          </Button>

          {showBackButton && config.recoverable && (
            <Button
              variant="text"
              onClick={() => router.back()}
              startIcon={<ArrowBack />}
              sx={{
                color: '#64748b',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Go Back
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

/**
 * Overlay error for modal-like display
 */
export function ErrorOverlay({
  errorType = ERROR_TYPES.UNKNOWN_ERROR,
  message,
  onRetry,
  onDismiss,
  retrying = false,
}) {
  const config = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR];
  const IconComponent = ERROR_ICONS[errorType] || ErrorOutline;
  const color = ERROR_COLORS[errorType] || ERROR_COLORS.default;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        p: 3,
      }}
      onClick={onDismiss}
    >
      <Paper
        elevation={8}
        sx={{
          maxWidth: 400,
          width: '100%',
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha(color, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <IconComponent sx={{ fontSize: 32, color }} />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
          {config.title}
        </Typography>
        <Typography sx={{ color: '#64748b', mb: 3, fontSize: '0.9rem' }}>
          {message || config.message}
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center">
          {onDismiss && (
            <Button
              variant="outlined"
              onClick={onDismiss}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#64748b',
              }}
            >
              Dismiss
            </Button>
          )}
          {config.recoverable && onRetry && (
            <Button
              variant="contained"
              onClick={onRetry}
              disabled={retrying}
              startIcon={
                retrying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Refresh sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: color,
                '&:hover': { bgcolor: alpha(color, 0.8) },
              }}
            >
              {retrying ? 'Retrying...' : config.action}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

/**
 * Enhanced network status banner with better design
 */
export function OfflineBanner({ onRetry }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
        color: '#fff',
        py: 1.5,
        px: 3,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        animation: 'slideDown 0.3s ease-out',
        '@keyframes slideDown': {
          '0%': { transform: 'translateY(-100%)' },
          '100%': { transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <WifiOff sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>You're offline</Typography>
        <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
          Showing cached data • Check your connection
        </Typography>
      </Box>
      {onRetry && (
        <Button
          size="small"
          onClick={onRetry}
          sx={{
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.2)',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 2,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
          }}
        >
          Retry
        </Button>
      )}
    </Box>
  );
}

export default ErrorPage;
